'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '../lib/i18n';
import { useEscapeClose } from '../lib/useEscapeClose';
import { useExitAnimation } from '../lib/useExitAnimation';
import { useToast } from './Toast';
import ConfirmModal from './ConfirmModal';
import EventFormFields from './EventFormFields';
import EventNotesSection from './EventNotesSection';
import EventEmailLinksSection from './EventEmailLinksSection';
import EntityHistoryPanel from './EntityHistoryPanel';
import EntityAuditTab from './EntityAuditTab';
import { useEventForm } from './useEventForm';
import { usePageTitle } from '../lib/usePageTitle';
import { IMP_ICONS, useImpulseLabels } from '../lib/impulses';
import { apiFetch } from '../lib/api';
import Link from 'next/link';

export type EventType = 'TASK' | 'MEETING' | 'CALL' | 'IM' | 'NOTE';
export type ImChannel = 'WHATSAPP' | 'FACEBOOK' | 'INSTAGRAM';
export type EventStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type CallResult = 'CONNECTED' | 'NOT_CONNECTED' | 'BUSY' | 'AGREED' | 'DECLINED' | 'CALL_BACK';

export type ChecklistItem = {
  id?: number;
  eventId?: number;
  text: string;
  done: boolean;
  sortOrder: number;
};

export type EventReminder = {
  id?: number;
  eventId?: number;
  minutes: number | string;
  type: 'APP' | 'EMAIL';
  refPoint?: 'START' | 'DEADLINE' | 'ABSOLUTE';
  sent: boolean;
  absoluteTime?: string | null;
};

export type UserOption = {
  id: number;
  name: string;
  email: string;
  role: string;
};

export type TeamOption = {
  id: number;
  name: string;
};

export type CalendarEvent = {
  id: number;
  userId: number;
  type: EventType;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
  status: EventStatus;
  priority: string | null;
  location: string | null;
  participants: string | null;
  phoneNumber: string | null;
  callResult: string | null;
  imChannel: string | null;
  durationMinutes: number | null;
  reminderMinutes: number | null;
  recurrenceRule: string | null;
  recurrenceEndDate: string | null;
  dueDate: string | null;
  color: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  checklist?: ChecklistItem[];
  reminders?: EventReminder[];
  assigneeIds?: number[];
  watcherIds?: number[];
  teamId?: number | null;
  refType?: string | null;
  refId?: string | null;
  accountId?: number | null;
  accountName?: string | null;
  accountPohodaId?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  leadId?: number | null;
  leadName?: string | null;
  isPrivate?: boolean;
  googleSync?: boolean;
  googleEventId?: string | null;
  googleCalendarName?: string | null;
  googleCalendarColor?: string | null;
  ownerName?: string | null;
  impulseType?: string | null;
  impulseKey?: string | null;
  snoozedUntil?: string | null;
  modifiedAt?: string | null;
  modifiedByName?: string | null;
  modifiedById?: number | null;
  createdByName?: string | null;
  createdById?: number | null;
};

// --- Shared helpers (exported for reuse by list/calendar pages) ---

export function toLocalDateTimeInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function toLocalDateInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function toLocalTimeInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function combineDateTime(dateStr: string, timeStr: string): string {
  return `${dateStr}T${timeStr}`;
}

export function formatDateTime(iso: string, locale: string): string {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return dt.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function getRecurrenceRule(recType: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY', interval = 1): string | null {
  if (recType === 'NONE') return null;
  const base = `FREQ=${recType}`;
  return interval > 1 ? `${base};INTERVAL=${interval}` : base;
}

export function parseRecurrenceType(rule: string | null): 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' {
  if (!rule) return 'NONE';
  if (rule.includes('DAILY')) return 'DAILY';
  if (rule.includes('WEEKLY')) return 'WEEKLY';
  if (rule.includes('MONTHLY')) return 'MONTHLY';
  if (rule.includes('YEARLY')) return 'YEARLY';
  return 'NONE';
}

export function parseRecurrenceInterval(rule: string | null): number {
  if (!rule) return 1;
  const match = rule.match(/INTERVAL=(\d+)/);
  if (match) {
    const n = parseInt(match[1], 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }
  return 1;
}

// --- Component ---

export type EventFormModalProps = {
  open: boolean;
  event: CalendarEvent | null;
  defaultType: EventType;
  allowTypeChange: boolean;
  defaultDate?: Date;
  defaultEndDate?: Date;
  /** Defaults for a NEW event (e.g. bind to entity via refType/refId or contact/account/lead). */
  newEventDefaults?: {
    title?: string | null; description?: string | null;
    refType?: string | null; refId?: string | null;
    accountId?: number | null; accountName?: string | null; accountPohodaId?: string | null;
    contactId?: number | null; contactName?: string | null;
    leadId?: number | null; leadName?: string | null;
    phoneNumber?: string | null;
    impulseType?: string | null; impulseKey?: string | null;
  } | null;
  userOptions: UserOption[];
  teamOptions: TeamOption[];
  editLabel?: string;
  newLabel?: string;
  onClose: () => void;
  onSaved: (event: CalendarEvent, isNew: boolean) => void;
  onDeleted: (id: number) => void;
};

export default function EventFormModal({
  open,
  event,
  defaultType,
  allowTypeChange,
  defaultDate,
  defaultEndDate,
  newEventDefaults,
  userOptions,
  teamOptions,
  editLabel,
  newLabel,
  onClose,
  onSaved,
  onDeleted,
}: EventFormModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const impulseLabelOf = useImpulseLabels();

  const form = useEventForm({
    event,
    defaultType,
    defaultDate,
    defaultEndDate,
    newEventDefaults,
    active: open,
    onSaved,
    onDeleted,
    onClose,
    editLabel,
    newLabel,
  });

  const { editingEvent, setEditingEvent, setFormStatus, deleteId, setDeleteId, saving, isDirty, handleSave, handleDelete, autoSaveState } = form;

  const [activeTab, setActiveTab] = useState<'detail' | 'notes' | 'emails' | 'history' | 'system'>('detail');
  useEffect(() => { setActiveTab('detail'); }, [editingEvent?.id, open]);

  usePageTitle(open ? (form.formTitle || (editingEvent?.id != null ? editingEvent.title : null)) : null);

  const headerEdit = editLabel || t('calendarEdit');
  const headerNew = newLabel || t('calendarNewEvent');

  useEscapeClose(open, onClose);

  // Quick one-click status toggle: mark the event done / reopen it,
  // without going through the full form save.
  const handleMarkDone = async () => {
    if (!editingEvent?.id) return;
    const next: EventStatus = editingEvent.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
    try {
      await apiFetch(`/api/user/calendar/events/${editingEvent.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      showToast(t(next === 'COMPLETED' ? 'calendarStatusCompleted' : 'eventReopen'), 'success');
      onSaved(editingEvent, false);
      onClose();
    } catch {
      showToast(t('toastUpdateFailed'), 'error');
    }
  };
  const exiting = useExitAnimation(open);

  if (!open && !exiting) return null;

  return (
    <>
      <div className={`fixed inset-0 z-[9999] flex items-center justify-center bg-bark/50 backdrop-blur-sm ${open ? 'animate-backdropIn' : 'animate-backdropOut'}`}>
        <div className={`bg-linen border border-wheat rounded-rustic shadow-xl p-6 max-w-7xl w-full mx-4 max-h-[94vh] overflow-y-auto ${open ? 'animate-modalIn' : 'animate-modalOut'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-bark flex items-center gap-2">
                {editingEvent ? headerEdit : headerNew}
              </h2>
              {form.formImpulseType && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-sky/15 text-sky-dark whitespace-nowrap"
                      title={impulseLabelOf(form.formImpulseType)}>
                  {IMP_ICONS[form.formImpulseType] ?? '📌'} {impulseLabelOf(form.formImpulseType)}
                </span>
              )}
              {editingEvent?.id && autoSaveState !== 'idle' && (
                <span className={`text-xs font-medium ${autoSaveState === 'saving' ? 'text-wood animate-pulse' : autoSaveState === 'saved' ? 'text-moss' : 'text-rust'}`}>
                  {autoSaveState === 'saving' ? '…' : autoSaveState === 'saved' ? '✓' : '⚠'}
                </span>
              )}
            </div>
            <button onClick={onClose} className="text-wood hover:text-bark text-xl leading-none px-2" title={t('calendarCancel')}>✕</button>
          </div>
          {editingEvent?.id != null && (
            <div className="flex gap-1 border-b border-wheat mb-4 flex-wrap">
              {(['detail', 'notes', 'emails', 'history', 'system'] as const).map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${activeTab === tab ? 'border-moss text-moss' : 'border-transparent text-wood hover:text-bark'}`}>
                  {tab === 'detail' ? t('brandTabGeneral') : tab === 'notes' ? t('notes') : tab === 'emails' ? t('leadEmails') : tab === 'history' ? t('entityHistory') : t('invTabSystem')}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'history' && editingEvent?.id != null ? (
            <EntityHistoryPanel entityType="CalendarEvent" entityId={editingEvent.id} />
          ) : activeTab === 'system' && editingEvent?.id != null ? (
            <EntityAuditTab
              id={editingEvent.id}
              createdAt={editingEvent.createdAt}
              modifiedAt={editingEvent.modifiedAt || editingEvent.updatedAt}
              createdByName={editingEvent.createdByName}
              createdById={editingEvent.createdById}
              modifiedByName={editingEvent.modifiedByName}
              modifiedById={editingEvent.modifiedById}
              extraIds={[
                { label: 'refType', value: editingEvent.refType },
                { label: 'refId', value: editingEvent.refId },
                { label: 'impulseKey', value: editingEvent.impulseKey },
              ]}
            />
          ) : activeTab === 'notes' && editingEvent?.id != null ? (
            <EventNotesSection eventType="CALENDAR" eventId={editingEvent.id} />
          ) : activeTab === 'emails' && editingEvent?.id != null ? (
            <EventEmailLinksSection eventId={editingEvent.id} contactId={form.formContactId} accountPohodaId={form.formAccountPohodaId} eventTitle={form.formTitle} />
          ) : (
            <EventFormFields form={form} allowTypeChange={allowTypeChange} userOptions={userOptions} teamOptions={teamOptions} onLinkClick={onClose} />
          )}

          {/* Actions — sticky footer, stále viditelný při scrollu modalu */}
          <div className="flex items-center justify-between mt-5 sticky bottom-0 z-10 -mx-6 -mb-6 px-6 pb-6 pt-3 bg-linen border-t border-wheat/60">
            <div className="flex gap-2">
              {editingEvent && (
                <button onClick={() => setDeleteId(editingEvent.id)}
                  className="text-sm px-4 py-2 rounded-rustic border border-rust/30 text-rust-dark hover:bg-rust/20 font-medium">
                  {t('calendarDelete')}
                </button>
              )}
              {editingEvent && (
                <button onClick={() => { setEditingEvent(null); setFormStatus('PENDING'); }}
                  className="text-sm px-4 py-2 rounded-rustic border border-wheat text-wood hover:bg-linen font-medium">
                  {t('calendarCopy')}
                </button>
              )}
              {editingEvent?.id != null && (
                <button onClick={handleMarkDone}
                  className="text-sm px-4 py-2 rounded-rustic border border-moss/40 text-moss-dark hover:bg-moss/10 font-medium">
                  {editingEvent.status === 'COMPLETED' ? `↩ ${t('eventReopen')}` : `✓ ${t('eventMarkDone')}`}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {editingEvent && (() => {
                const owner = (userOptions || []).find(u => u.id === editingEvent.userId);
                return owner && (
                  <span className="text-xs text-wood">
                    {t('calendarOwner')}: <Link href={`/users/${owner.id}`} className="hover:text-moss hover:underline" onClick={onClose}>{owner.name}</Link>
                  </span>
                );
              })()}
              <div className="flex gap-2">
                <button onClick={onClose} className="btn-secondary px-4 py-2">{t('calendarCancel')}</button>
                <button onClick={() => handleSave(false)} disabled={saving || !isDirty} className="btn-secondary px-4 py-2 disabled:opacity-50">{saving ? '…' : t('calendarSave')}</button>
                <button onClick={() => handleSave(true)} disabled={saving || !isDirty} className="btn-primary px-4 py-2 disabled:opacity-50">{saving ? '…' : t('calendarSaveClose')}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={deleteId !== null}
        title={t('calendarDelete')}
        message={t('calendarDeleteConfirm')}
        confirmLabel={t('calendarDelete')}
        cancelLabel={t('calendarCancel')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
}
